import React, { useState, useEffect, useCallback } from 'react';
import { Text, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { Money24Regular, CalendarLtr24Regular, ArrowTrending24Regular } from '@fluentui/react-icons';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { api } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface InsightsPanelProps {
  accountId: number | null;
}

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
  },
  mobileContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  cardIcon: {
    color: tokens.colorBrandForeground1,
  },
  spendingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  spendingBar: {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: tokens.colorBrandBackground,
    marginTop: '2px',
  },
  recurringItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  negative: { color: '#c0392b', fontWeight: 600 },
  positive: { color: '#27ae60', fontWeight: 600 },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '60px',
    paddingBottom: '60px',
  },
});

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export const InsightsPanel: React.FC<InsightsPanelProps> = ({ accountId }) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    if (!accountId) { setLoading(false); return; }
    try {
      setLoading(true);
      const result = await api.getInsights(accountId);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  if (loading) {
    return <div className={styles.loading}><Spinner label="Loading insights..." size="large" /></div>;
  }

  if (!data) {
    return <Text>No insights available.</Text>;
  }

  const maxSpending = data.spending.length > 0
    ? Math.max(...data.spending.map((s: any) => s.totalPayment))
    : 1;

  return (
    <div className={isMobile ? styles.mobileContainer : styles.container}>
      {/* Spending This Year */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          <Money24Regular className={styles.cardIcon} />
          <Text weight="semibold" size={400}>Spending This Year</Text>
        </div>
        {data.spending.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No transactions this year</Text>
        ) : (
          data.spending.map((s: any, i: number) => (
            <div key={i}>
              <div className={styles.spendingItem}>
                <div>
                  <Text size={200}>{s.payee}</Text>
                  <Text size={100} style={{ color: tokens.colorNeutralForeground3, marginLeft: '8px' }}>
                    ({s.txnCount}x)
                  </Text>
                </div>
                <Text size={200} weight="semibold" className={styles.negative}>{formatMoney(s.totalPayment)}</Text>
              </div>
              <div className={styles.spendingBar}
                style={{ width: `${(s.totalPayment / maxSpending) * 100}%` }} />
            </div>
          ))
        )}
      </div>

      {/* Recurring Payments */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          <CalendarLtr24Regular className={styles.cardIcon} />
          <Text weight="semibold" size={400}>Recurring Payments</Text>
        </div>
        {data.recurringPayments.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Not enough data yet — keep adding transactions</Text>
        ) : (
          data.recurringPayments.map((r: any, i: number) => (
            <div key={i} className={styles.recurringItem}>
              <div>
                <Text size={200} weight="semibold" block>{r.payee}</Text>
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                  {r.monthCount} months
                </Text>
              </div>
              <Text size={200} className={styles.negative}>
                {formatMoney(r.avgAmount)}/mo
              </Text>
            </div>
          ))
        )}
      </div>

      {/* Recurring Deposits */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          <ArrowTrending24Regular className={styles.cardIcon} />
          <Text weight="semibold" size={400}>Recurring Deposits</Text>
        </div>
        {data.recurringDeposits.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Not enough data yet — keep adding transactions</Text>
        ) : (
          data.recurringDeposits.map((r: any, i: number) => (
            <div key={i} className={styles.recurringItem}>
              <div>
                <Text size={200} weight="semibold" block>{r.payee}</Text>
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                  {r.monthCount} months
                </Text>
              </div>
              <Text size={200} className={styles.positive}>
                {formatMoney(r.avgAmount)}/mo
              </Text>
            </div>
          ))
        )}
      </div>
      {/* Monthly Income vs Expenses Chart */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          <ArrowTrending24Regular className={styles.cardIcon} />
          <Text weight="semibold" size={400}>Monthly Income vs Expenses</Text>
        </div>
        {data.monthly.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No data yet</Text>
        ) : (
          <Bar
            data={{
              labels: data.monthly.map((m: any) => m.label),
              datasets: [
                {
                  label: 'Income',
                  data: data.monthly.map((m: any) => m.totalIncome),
                  backgroundColor: 'rgba(39, 174, 96, 0.7)',
                  borderColor: '#27ae60',
                  borderWidth: 1,
                  borderRadius: 4,
                },
                {
                  label: 'Expenses',
                  data: data.monthly.map((m: any) => m.totalExpenses),
                  backgroundColor: 'rgba(192, 57, 43, 0.7)',
                  borderColor: '#c0392b',
                  borderWidth: 1,
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y ?? 0)}`,
                  },
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (val) => '$' + Number(val).toLocaleString(),
                  },
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};
